import type { Page } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import { useCallback, useRef, useState } from "react";
import { useDirectUploadMultiple } from "@/hooks/use-direct-upload-multiple";

export interface AdminPhoto {
  id: string;
  position: number;
  image: { src: string; width?: number; height?: number } | null;
}

export interface AdminPhotoManagerPaths {
  indexPath: string;
  photoPath: (sqid: string) => string;
  reorderPath: string;
  createStockPath?: string;
  stockSearchPath?: string;
}

export interface AdminPhotoManagerConfig<P extends AdminPhoto> {
  initialPhotos: P[];
  paths: AdminPhotoManagerPaths;
  extractPhotos: (page: Page) => P[] | null;
}

export interface AdminPhotoManager<P extends AdminPhoto> {
  cancelDelete: () => void;
  closeUploadModal: () => void;
  confirmDelete: () => void;
  confirmUpload: () => void;
  deleteTarget: string | null;
  isUploadModalOpen: boolean;
  openUploadModal: () => void;
  photos: P[];
  reorderPhotos: (orderedIds: string[]) => void;
  requestDelete: (photoId: string) => void;
  submitStockPhoto: (stockPhotoUrl: string) => void;
  hasStockPhotoSupport: boolean;
  stockSearchPath: string | null;
  upload: ReturnType<typeof useDirectUploadMultiple>;
}

export function useAdminPhotoManager<P extends AdminPhoto>({
  initialPhotos,
  paths,
  extractPhotos,
}: AdminPhotoManagerConfig<P>): AdminPhotoManager<P> {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [photos, setPhotos] = useState(initialPhotos);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const upload = useDirectUploadMultiple();

  const openUploadModal = useCallback(() => {
    upload.reset();
    setIsUploadModalOpen(true);
  }, [upload]);

  const closeUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
    upload.reset();
  }, [upload]);

  const confirmUpload = useCallback(() => {
    if (upload.completedSignedIds.length === 0) return;

    router.post(
      paths.indexPath,
      { signed_ids: upload.completedSignedIds },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          closeUploadModal();
          const updated = extractPhotos(page);
          if (updated) setPhotos(updated);
        },
      },
    );
  }, [upload.completedSignedIds, paths.indexPath, closeUploadModal, extractPhotos]);

  const submitStockPhoto = useCallback(
    (stockPhotoUrl: string) => {
      if (!paths.createStockPath) return;
      router.post(
        paths.createStockPath,
        { stock_photo_url: stockPhotoUrl },
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: (page) => {
            const updated = extractPhotos(page);
            if (updated) setPhotos(updated);
          },
        },
      );
    },
    [paths.createStockPath, extractPhotos],
  );

  const reorderPhotos = useCallback(
    (orderedIds: string[]) => {
      const before = photosRef.current;
      const reordered = orderedIds.map((id) => before.find((p) => p.id === id)).filter(Boolean) as P[];
      setPhotos(reordered.map((p, i) => ({ ...p, position: i + 1 })));

      router.patch(
        paths.reorderPath,
        { photo_ids: orderedIds },
        {
          preserveState: true,
          preserveScroll: true,
          onError: () => setPhotos(before),
          onSuccess: (page) => {
            const updated = extractPhotos(page);
            if (updated) setPhotos(updated);
          },
        },
      );
    },
    [paths.reorderPath, extractPhotos],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;

    router.delete(paths.photoPath(deleteTarget), {
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        setDeleteTarget(null);
        const updated = extractPhotos(page);
        if (updated) setPhotos(updated);
      },
    });
  }, [deleteTarget, paths, extractPhotos]);

  return {
    cancelDelete: useCallback(() => setDeleteTarget(null), []),
    closeUploadModal,
    confirmDelete,
    confirmUpload,
    deleteTarget,
    isUploadModalOpen,
    openUploadModal,
    photos,
    reorderPhotos,
    requestDelete: setDeleteTarget,
    submitStockPhoto,
    hasStockPhotoSupport: Boolean(paths.createStockPath && paths.stockSearchPath),
    stockSearchPath: paths.stockSearchPath ?? null,
    upload,
  };
}
